import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircleIcon, DownloadIcon, AlertTriangleIcon, ShieldCheckIcon } from 'lucide-react';
import { formatNaira } from '../utils/format';
import { TopBar } from '../components/TopBar';
interface TransactionDetailProps {
  navigate: (screen: string) => void;
}
export function TransactionDetail({
  navigate
}: TransactionDetailProps) {
  // Hardcoded sample transaction matching the first one in Wallet
  const tx = {
    type: 'Premium Payment',
    title: 'Child Education',
    amount: -16666,
    status: 'completed',
    date: 'Mar 15, 2026',
    time: '2:34 PM',
    ref: 'TXN-2026031500001',
    desc: 'Monthly premium payment for Child Education plan',
    from: 'Skydd Wallet',
    to: 'Leadway Assurance Plc'
  };
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.95
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 300
  }} className="flex-1 overflow-y-auto pb-safe bg-slate-50 flex flex-col h-full" data-id="element-848">
      <TopBar title="Transaction" showBack onBack={() => navigate('wallet')} data-id="element-849" />

      <div className="px-5 pt-8 pb-6 flex-1 flex flex-col" data-id="element-850">
        {/* Header */}
        <div className="text-center mb-8" data-id="element-851">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-500 mx-auto mb-4 border-4 border-white shadow-lg shadow-emerald-500/20" data-id="element-852">
            <CheckCircleIcon className="w-10 h-10" data-id="element-853" />
          </div>

          <h1 className="text-4xl font-bold text-[#1B3B36] mb-2 tracking-tight" data-id="element-854">
            {formatNaira(Math.abs(tx.amount))}
          </h1>

          <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-855">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-856"></div>{' '}
            Completed
          </div>
        </div>

        {/* Details Card */}
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 mb-8" data-id="element-857">
          <div className="flex items-center gap-3 pb-5 border-b border-gray-50 mb-5" data-id="element-858">
            <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center text-[#1B3B36]" data-id="element-859">
              <ShieldCheckIcon className="w-5 h-5" data-id="element-860" />
            </div>
            <div data-id="element-861">
              <h3 className="font-bold text-[#1B3B36]" data-id="element-862">{tx.type}</h3>
              <p className="text-xs text-gray-500" data-id="element-863">{tx.title}</p>
            </div>
          </div>

          <div className="space-y-4" data-id="element-864">
            <div className="flex justify-between items-start" data-id="element-865">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-866">
                Date & Time
              </span>
              <div className="text-right" data-id="element-867">
                <p className="text-sm font-bold text-[#1B3B36]" data-id="element-868">{tx.date}</p>
                <p className="text-xs text-gray-500" data-id="element-869">{tx.time}</p>
              </div>
            </div>

            <div className="flex justify-between items-start" data-id="element-870">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-871">
                Reference
              </span>
              <p className="text-sm font-mono font-bold text-[#1B3B36]" data-id="element-872">
                {tx.ref}
              </p>
            </div>

            <div className="flex justify-between items-start" data-id="element-873">
              <span className="text-xs font-bold text-gray-400 uppercase tracking-wider w-24" data-id="element-874">
                Description
              </span>
              <p className="text-sm font-medium text-[#1B3B36] text-right" data-id="element-875">
                {tx.desc}
              </p>
            </div>

            <div className="pt-4 mt-4 border-t border-gray-50 border-dashed" data-id="element-876">
              <div className="flex justify-between items-center mb-3" data-id="element-877">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-878">
                  From
                </span>
                <p className="text-sm font-bold text-[#1B3B36]" data-id="element-879">{tx.from}</p>
              </div>
              <div className="flex justify-between items-center" data-id="element-880">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-881">
                  To
                </span>
                <p className="text-sm font-bold text-[#1B3B36]" data-id="element-882">{tx.to}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-auto space-y-4" data-id="element-883">
          <button className="w-full py-4 rounded-xl border-2 border-gray-200 text-[#1B3B36] font-bold text-sm hover:border-gray-300 hover:bg-gray-50 transition-colors active:scale-95 flex items-center justify-center gap-2" data-id="element-884">
            <DownloadIcon className="w-4 h-4" data-id="element-885" /> Download Receipt
          </button>

          <div className="text-center" data-id="element-886">
            <button className="text-xs font-bold text-gray-400 hover:text-rose-500 transition-colors flex items-center justify-center gap-1 mx-auto" data-id="element-887">
              <AlertTriangleIcon className="w-3 h-3" data-id="element-888" /> Report an Issue
            </button>
          </div>
        </div>
      </div>
    </motion.div>;
}